import React, { useState, useEffect } from 'react'
import { useOutletContext, useNavigate } from 'react-router-dom'
import { portalRequest } from '../../utils/portalApi'
import { 
  Bell, CheckCheck, Trash2, Calendar, Users, MessageCircle, 
  CreditCard, Trophy, Info, Clock
} from 'lucide-react'

const typeIcons = {
  event: Calendar,
  team: Trophy,
  message: MessageCircle,
  payment: CreditCard,
  roster: Users,
  default: Info,
}

function timeAgo(dateStr) {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  const now = new Date()
  const diff = Math.floor((now - d) / 1000)
  if (diff < 60) return 'just now'
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export default function PortalNotifications() {
  const { token } = useOutletContext()
  const navigate = useNavigate()
  const [notifications, setNotifications] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchNotifications()
  }, [])

  const fetchNotifications = async () => {
    try {
      const data = await portalRequest('/api/notifications')
      setNotifications(Array.isArray(data) ? data : data?.notifications || [])
    } catch (err) {
      console.error('Error fetching notifications:', err)
    } finally {
      setLoading(false)
    }
  }

  const markRead = async (notifId) => {
    try {
      await portalRequest(`/api/notifications/${notifId}/read`, { method: 'POST' })
      setNotifications(prev => prev.map(n => 
        (n.notification_id || n._id) === notifId ? { ...n, read: true } : n
      ))
    } catch {}
  }

  const markAllRead = async () => {
    try {
      await portalRequest('/api/notifications/read-all', { method: 'POST' })
      setNotifications(prev => prev.map(n => ({ ...n, read: true })))
    } catch {}
  }

  const clearAll = async () => {
    try {
      await portalRequest('/api/notifications/clear-all', { method: 'DELETE' })
      setNotifications([])
    } catch {}
  }

  const unreadCount = notifications.filter(n => !n.read).length

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-500"></div>
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            Notifications
            {unreadCount > 0 && (
              <span className="text-sm px-2.5 py-0.5 rounded-full bg-green-500/10 text-green-400 font-medium">
                {unreadCount} new
              </span>
            )}
          </h1>
          <p className="text-zinc-400 mt-1">Stay updated on team activity</p>
        </div>
        {notifications.length > 0 && (
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="flex items-center gap-1.5 px-3 py-2 text-sm text-zinc-400 hover:text-white border border-zinc-700 rounded-lg hover:border-zinc-600 transition-colors"
              >
                <CheckCheck className="w-4 h-4" />
                Mark all read
              </button>
            )}
            <button
              onClick={clearAll}
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-zinc-400 hover:text-red-400 border border-zinc-700 rounded-lg hover:border-red-900 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Clear
            </button>
          </div>
        )}
      </div>

      {notifications.length === 0 ? (
        <div className="bg-zinc-800 rounded-2xl border border-zinc-700 p-12 text-center">
          <Bell className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-400 font-medium">No notifications</p>
          <p className="text-zinc-500 text-sm mt-1">You're all caught up!</p>
        </div>
      ) : (
        <div className="space-y-2">
          {notifications.map(notif => {
            const id = notif.notification_id || notif._id
            const Icon = typeIcons[notif.type] || typeIcons.default

            return (
              <div
                key={id}
                onClick={() => {
                  if (!notif.read) markRead(id)
                  // Navigate to relevant page if possible
                  if (notif.link) navigate(notif.link)
                }}
                className={`flex items-start gap-4 p-4 rounded-xl border transition-colors cursor-pointer ${
                  notif.read 
                    ? 'bg-zinc-800/50 border-zinc-700/50 hover:bg-zinc-800' 
                    : 'bg-zinc-800 border-zinc-700 hover:bg-zinc-750'
                }`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                  notif.read ? 'bg-zinc-700/50' : 'bg-green-500/10'
                }`}>
                  <Icon className={`w-5 h-5 ${notif.read ? 'text-zinc-500' : 'text-green-400'}`} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <p className={`text-sm ${notif.read ? 'text-zinc-400' : 'text-white font-medium'}`}>
                      {notif.title || notif.message}
                    </p>
                    {!notif.read && (
                      <div className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0 mt-1.5"></div>
                    )}
                  </div>
                  {notif.body && notif.body !== notif.title && (
                    <p className="text-xs text-zinc-500 mt-0.5 line-clamp-2">{notif.body}</p>
                  )}
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <Clock className="w-3 h-3 text-zinc-600" />
                    <span className="text-xs text-zinc-500">{timeAgo(notif.created_at)}</span>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
